import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isRTL } from "@/i18n";
import { submitContactForm } from "@/services/contactService";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumb from "@/components/Breadcrumb";

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const rtl = isRTL(i18n.language);
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  
  const offices = t("contact_page.offices", { returnObjects: true }) || [];
  const subjectOptions = t("contact_page.form_subject_options", { returnObjects: true }) || [];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      await submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: subject,
        message: formData.message,
        inquiryType: 'general',
        language: i18n.language
      });

      setFormSent(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSubject('');
      setTimeout(() => setFormSent(false), 4000);
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setFormError(t("contact_page.form_error") || "Failed to send message. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet><title>{t("contact_page.seo_title")}</title><meta name="description" content={t("contact_page.seo_desc")} /></Helmet>
      <Header />
      <PageHeader title={t("contact_page.hero_title")} highlight={t("contact_page.hero_highlight")} subtitle={t("contact_page.hero_subtitle")}>
        <Breadcrumb items={[{ label: t("breadcrumb.contact") }]} />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("contact_page.form_pretitle")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t("contact_page.form_title")}</h2>

            {formSent ? (
              <div data-testid="form-success" className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-sm p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-[#25D366] mx-auto mb-4" />
                <p className="text-[#0A192F] font-medium text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("contact_page.form_success")}</p>
              </div>
            ) : (
              <>
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                    <p className="text-red-700 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{formError}</p>
                  </div>
                )}
                <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="contact-name" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t("contact_page.form_name")} required className="w-full px-5 py-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                    <input data-testid="contact-email" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t("contact_page.form_email")} required className="w-full px-5 py-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="contact-phone" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t("contact_page.form_phone")} className="w-full px-5 py-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                    <Select value={subject} onValueChange={setSubject}>
                      <SelectTrigger data-testid="contact-subject" className="h-auto px-5 py-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:ring-0 focus:border-[#D4AF37]">
                        <SelectValue placeholder={t("contact_page.form_subject")} />
                      </SelectTrigger>
                      <SelectContent>
                        {subjectOptions.map((opt, i) => (
                          <SelectItem key={i} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <textarea data-testid="contact-message" name="message" value={formData.message} onChange={handleInputChange} placeholder={t("contact_page.form_message")} rows={5} required className="w-full px-5 py-4 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none" style={{ fontFamily: "'Outfit', sans-serif" }} />
                  <button data-testid="contact-submit" type="submit" disabled={formLoading} className="flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-white font-medium rounded-sm hover:bg-[#B5952F] transition-colors shadow-lg shadow-[#D4AF37]/20 disabled:opacity-50 disabled:cursor-not-allowed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <Send className="w-4 h-4" />{formLoading ? (t("contact_page.sending") || "Sending...") : t("contact_page.form_send")}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Office Cards */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("contact_page.offices_pretitle")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t("contact_page.offices_title")}</h2>

            {offices.map((office, i) => (
              <div key={i} data-testid={`office-card-${i}`} className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><MapPin className="w-4 h-4 text-[#D4AF37]" /></div>
                  <div>
                    <h3 className="text-base font-semibold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>{office.city}</h3>
                    <p className="text-xs text-[#D4AF37]" style={{ fontFamily: "'Outfit', sans-serif" }}>{office.type}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#4A5568] shrink-0 mt-0.5" /><span className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{office.address}</span></div>
                  <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#4A5568] shrink-0" /><a href={`tel:${office.phone.replace(/\s/g, "")}`} className="text-sm text-[#4A5568] hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{office.phone}</a></div>
                  <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#4A5568] shrink-0" /><a href={`mailto:${office.email}`} className="text-sm text-[#4A5568] hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{office.email}</a></div>
                  <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#4A5568] shrink-0" /><span className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{office.hours}</span></div>
                </div>
              </div>
            ))}

            {/* WhatsApp CTA */}
            <a href="https://wa.me/905338000000" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-[#25D366] text-white font-medium rounded-sm hover:bg-[#128C7E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.121 1.521 5.856L0 24l6.335-1.652A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.875 0-3.65-.516-5.178-1.41l-.372-.22-3.86 1.01 1.038-3.785-.242-.385A9.693 9.693 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z" /></svg>
              WhatsApp
            </a>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16" data-testid="contact-map">
          <h2 className="text-xl font-bold text-[#0A192F] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t("contact_page.map_title")}</h2>
          <div className="h-[400px] rounded-sm overflow-hidden border border-[#E2E8F0]">
            <iframe title="Office Location" width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d30000!2d33.3182!3d35.3362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2s"
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
